import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerRecurringFromTransactionDialog from "./center/OwnerRecurringFromTransactionDialog";

const ACCOUNT_TYPES = [
  ["bank", "Banco"], ["wallet", "Billetera"], ["cash", "Efectivo"],
  ["investment", "Inversión"], ["receivable", "Por cobrar"], ["debt", "Deuda"], ["other", "Otra"],
];
const AREAS = ["Personal", "Hogar", "Comida", "Freelancer", "Carrera", "Negocios"];
const TRANSACTION_TYPES = [
  ["expense", "Gasto"], ["income", "Ingreso"], ["transfer", "Transferencia"], ["adjustment", "Ajuste"],
];

function money(value, currency) {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function todayInputValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

export default function OwnerFinancePage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = React.useState([]);
  const [transactions, setTransactions] = React.useState([]);
  const [recurring, setRecurring] = React.useState(null);
  const [transactionToRecurring, setTransactionToRecurring] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState({ name: "", type: "bank", currency: "ARS", openingBalance: "" });
  const [movement, setMovement] = React.useState({
    type: "expense", amount: "", accountId: "", fromAccountId: "", toAccountId: "",
    adjustmentDirection: "increase", area: "Personal", category: "", description: "", occurredAt: todayInputValue(),
  });

  const load = React.useCallback(async () => {
    setError("");
    try {
      const [accountData, transactionData, recurringData] = await Promise.all([
        ownerApi.listFinanceAccounts(),
        ownerApi.listFinanceTransactions({ limit: 80 }),
        ownerApi.listRecurringFinance(),
      ]);
      setAccounts(accountData.accounts || []);
      setTransactions(transactionData.transactions || []);
      setRecurring(recurringData.recurring || null);
    } catch (err) {
      setError(formatApiError(err, "No se pudieron cargar tus finanzas"));
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const createAccount = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await ownerApi.createFinanceAccount({ ...accountForm, openingBalance: Number(accountForm.openingBalance || 0) });
      setAccountForm((current) => ({ ...current, name: "", openingBalance: "" }));
      await load();
    } catch (err) {
      setError(formatApiError(err, "No se pudo crear la cuenta"));
    } finally {
      setSaving(false);
    }
  };

  const createMovement = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await ownerApi.createFinanceTransaction({
        ...movement,
        amount: Number(movement.amount),
        occurredAt: new Date(`${movement.occurredAt}T12:00:00`).toISOString(),
        source: { kind: "manual" },
      });
      setMovement((current) => ({ ...current, amount: "", category: "", description: "" }));
      await load();
    } catch (err) {
      setError(formatApiError(err, "No se pudo registrar el movimiento"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ pb: 5 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/dashboard/owner")} sx={{ mb: 1, textTransform: "none", fontWeight: 900 }}>
        Owner Panel
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 1000, letterSpacing: "-.035em" }}>Mis Finanzas</Typography>
      <Typography sx={{ color: "text.secondary", mt: .5, mb: 2.5, maxWidth: 820 }}>
        Base privada e independiente de los proyectos. Los saldos se derivan de movimientos: no se duplican fuentes de verdad.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(320px,.8fr) minmax(0,1.6fr)" }, gap: 2, mb: 2 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent component="form" onSubmit={createAccount}>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Nueva cuenta</Typography>
            <Stack spacing={1.3}>
              <TextField label="Nombre" value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} required size="small" />
              <TextField select label="Tipo" value={accountForm.type} onChange={(e) => setAccountForm({ ...accountForm, type: e.target.value })} size="small">
                {ACCOUNT_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <TextField label="Moneda" value={accountForm.currency} onChange={(e) => setAccountForm({ ...accountForm, currency: e.target.value.toUpperCase() })} size="small" />
                <TextField label="Saldo inicial" type="number" value={accountForm.openingBalance} onChange={(e) => setAccountForm({ ...accountForm, openingBalance: e.target.value })} size="small" />
              </Box>
              <Button type="submit" variant="contained" startIcon={<AddRoundedIcon />} disabled={saving} sx={{ textTransform: "none", fontWeight: 950 }}>
                Crear cuenta
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Mi plata</Typography>
            {!accounts.length ? <Typography sx={{ color: "text.secondary" }}>Creá tu primera cuenta para empezar.</Typography> : null}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1 }}>
              {accounts.map((account) => (
                <Box key={account.id || account._id} sx={{ p: 1.5, borderRadius: 2.3, border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.10)" }}>
                  <Typography sx={{ fontWeight: 950 }}>{account.name}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 850 }}>{account.type} · {account.currency}</Typography>
                  <Typography variant="h6" sx={{ mt: .6, fontWeight: 1000 }}>{money(account.balance, account.currency)}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent component="form" onSubmit={createMovement}>
          <Typography variant="h6" sx={{ fontWeight: 950, mb: 1.5 }}>Registrar movimiento</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4,minmax(0,1fr))" }, gap: 1.2 }}>
            <TextField select label="Tipo" value={movement.type} onChange={(e) => setMovement({ ...movement, type: e.target.value })} size="small">
              {TRANSACTION_TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Monto" type="number" inputProps={{ min: "0", step: "any" }} value={movement.amount} onChange={(e) => setMovement({ ...movement, amount: e.target.value })} required size="small" />
            <TextField select label="Área" value={movement.area} onChange={(e) => setMovement({ ...movement, area: e.target.value })} size="small">
              {AREAS.map((area) => <MenuItem key={area} value={area}>{area}</MenuItem>)}
            </TextField>
            <TextField label="Fecha" type="date" value={movement.occurredAt} onChange={(e) => setMovement({ ...movement, occurredAt: e.target.value })} InputLabelProps={{ shrink: true }} size="small" />

            {movement.type === "transfer" ? (
              <>
                <TextField select label="Desde" value={movement.fromAccountId} onChange={(e) => setMovement({ ...movement, fromAccountId: e.target.value })} required size="small">
                  {accounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}
                </TextField>
                <TextField select label="Hacia" value={movement.toAccountId} onChange={(e) => setMovement({ ...movement, toAccountId: e.target.value })} required size="small">
                  {accounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}
                </TextField>
              </>
            ) : (
              <TextField select label="Cuenta" value={movement.accountId} onChange={(e) => setMovement({ ...movement, accountId: e.target.value })} required size="small">
                {accounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}
              </TextField>
            )}

            {movement.type === "adjustment" ? (
              <TextField select label="Sentido" value={movement.adjustmentDirection} onChange={(e) => setMovement({ ...movement, adjustmentDirection: e.target.value })} size="small">
                <MenuItem value="increase">Sumar saldo</MenuItem>
                <MenuItem value="decrease">Restar saldo</MenuItem>
              </TextField>
            ) : null}
            <TextField label="Categoría" value={movement.category} onChange={(e) => setMovement({ ...movement, category: e.target.value })} size="small" />
            <TextField label="Descripción" value={movement.description} onChange={(e) => setMovement({ ...movement, description: e.target.value })} size="small" sx={{ gridColumn: { md: "span 2" } }} />
          </Box>
          <Button type="submit" variant="contained" startIcon={movement.type === "transfer" ? <SyncAltRoundedIcon /> : <AddRoundedIcon />} disabled={saving || !accounts.length} sx={{ mt: 1.5, textTransform: "none", fontWeight: 950 }}>
            Guardar movimiento
          </Button>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 950 }}>Últimos movimientos</Typography>
          <Divider sx={{ my: 1.2 }} />
          {!transactions.length ? <Typography sx={{ color: "text.secondary" }}>Todavía no registraste movimientos.</Typography> : null}
          <Stack divider={<Divider flexItem />} spacing={0}>
            {transactions.map((transaction) => {
              const account = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.accountId));
              const from = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.fromAccountId));
              const to = accounts.find((candidate) => String(candidate.id || candidate._id) === String(transaction.toAccountId));
              const sign = transaction.type === "income" ? "+" : transaction.type === "expense" ? "-" : "";
              return (
                <Box key={transaction.id || transaction._id} sx={{ py: 1.25, display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>{transaction.description || transaction.category || transaction.type}</Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {transaction.type === "transfer" ? `${from?.name || "Origen"} → ${to?.name || "Destino"}` : account?.name || "Cuenta"}
                      {transaction.area ? ` · ${transaction.area}` : ""}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={.6} alignItems="center">
                    <Typography sx={{ whiteSpace: "nowrap", fontWeight: 1000 }}>{sign}{money(transaction.amount, transaction.currency)}</Typography>
                    {["expense", "income"].includes(transaction.type) ? (
                      transaction.recurringPlanId || (transaction.source?.kind === "recurring" && transaction.source?.entityId)
                        ? <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary" }}>Mensual</Typography>
                        : <Button size="small" startIcon={<RepeatRoundedIcon />} onClick={() => setTransactionToRecurring(transaction)} sx={{ textTransform: "none", fontWeight: 900, minWidth: 0 }}>Mensual</Button>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      <OwnerRecurringFromTransactionDialog
        open={Boolean(transactionToRecurring)}
        onClose={() => setTransactionToRecurring(null)}
        transaction={transactionToRecurring}
        recurring={recurring}
        onSaved={async () => { setTransactionToRecurring(null); await load(); }}
      />
    </Box>
  );
}
